import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Keys stored in sessionStorage for the current visit.
 * We persist in sessionStorage (not localStorage) so they survive
 * in-app navigation but don't leak across sessions.
 */
const STORAGE_KEYS = {
  fbclid: 'fk_fbclid',
  fbclidTs: 'fk_fbclid_ts',
  gclid: 'fk_gclid',
  utmSource: 'fk_utm_source',
  utmMedium: 'fk_utm_medium',
  utmCampaign: 'fk_utm_campaign',
  utmContent: 'fk_utm_content',
  utmTerm: 'fk_utm_term',
  landingPage: 'fk_landing_page',
} as const;

export interface TrackingParams {
  fbclid: string | null;
  gclid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  landing_page: string | null;
  /** Meta click id: the real _fbc cookie, or one synthesized from ?fbclid. */
  fbc: string | null;
  /** Meta browser-side cookie value (_fbp) — set by the Pixel. */
  fbp: string | null;
}

/** Read a cookie by name. */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Resolve the Meta click-id (_fbc) for server-side CAPI.
 *
 * Prefer the real _fbc cookie the Pixel writes (authoritative — the browser
 * Pixel sends the same value). On a first-touch ad click the Pixel may not
 * have converted ?fbclid into _fbc yet by the time the form submits, so we
 * synthesize it in Meta's documented format `fb.1.<clickTime>.<fbclid>`.
 * Without this, CAPI Leads from cold ad clicks carry no click attribution.
 */
function resolveFbc(): string | null {
  const real = getCookie('_fbc');
  if (real) return real;
  const fbclid = sessionStorage.getItem(STORAGE_KEYS.fbclid);
  if (!fbclid) return null;
  const ts = sessionStorage.getItem(STORAGE_KEYS.fbclidTs) || String(Date.now());
  return `fb.1.${ts}.${fbclid}`;
}

/**
 * Captures UTM parameters and click IDs (fbclid, gclid) from the URL
 * on initial page load and stores them in sessionStorage.
 *
 * Call once near the root of your public-facing page tree.
 * Subsequent calls (route changes) are no-ops for the capture step,
 * but the returned getter always reads current sessionStorage values.
 */
export function useTrackingParams(): void {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Only capture on the first load that has tracking params
    const fbclid = params.get('fbclid');
    const gclid = params.get('gclid');
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    const utmContent = params.get('utm_content');
    const utmTerm = params.get('utm_term');

    if (fbclid) {
      sessionStorage.setItem(STORAGE_KEYS.fbclid, fbclid);
      // Stamp the first-touch time so a synthesized _fbc uses the click time,
      // not the (later) form-submit time. Keep the earliest value.
      if (!sessionStorage.getItem(STORAGE_KEYS.fbclidTs)) {
        sessionStorage.setItem(STORAGE_KEYS.fbclidTs, String(Date.now()));
      }
    }
    if (gclid) sessionStorage.setItem(STORAGE_KEYS.gclid, gclid);
    if (utmSource) sessionStorage.setItem(STORAGE_KEYS.utmSource, utmSource);
    if (utmMedium) sessionStorage.setItem(STORAGE_KEYS.utmMedium, utmMedium);
    if (utmCampaign) sessionStorage.setItem(STORAGE_KEYS.utmCampaign, utmCampaign);
    if (utmContent) sessionStorage.setItem(STORAGE_KEYS.utmContent, utmContent);
    if (utmTerm) sessionStorage.setItem(STORAGE_KEYS.utmTerm, utmTerm);

    // Capture landing page only once per session
    if (!sessionStorage.getItem(STORAGE_KEYS.landingPage)) {
      sessionStorage.setItem(STORAGE_KEYS.landingPage, window.location.pathname);
    }
  }, [location.search]);
}

/**
 * Read all captured tracking params from sessionStorage + cookies.
 * Can be called outside of React (e.g. in an edge function call).
 */
export function getTrackingParams(): TrackingParams {
  return {
    fbclid: sessionStorage.getItem(STORAGE_KEYS.fbclid),
    gclid: sessionStorage.getItem(STORAGE_KEYS.gclid),
    utm_source: sessionStorage.getItem(STORAGE_KEYS.utmSource),
    utm_medium: sessionStorage.getItem(STORAGE_KEYS.utmMedium),
    utm_campaign: sessionStorage.getItem(STORAGE_KEYS.utmCampaign),
    utm_content: sessionStorage.getItem(STORAGE_KEYS.utmContent),
    utm_term: sessionStorage.getItem(STORAGE_KEYS.utmTerm),
    landing_page: sessionStorage.getItem(STORAGE_KEYS.landingPage),
    fbc: resolveFbc(),
    fbp: getCookie('_fbp'),
  };
}
